import { addWeeks } from '@util/date-util'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import type { Subproces } from '../types/Subproces'

import {
  type StepConfig,
  canEditSubproces,
  generateStepStatuses,
} from './subproces-util'

export { canEditBodem, generateBodemStepStatuses, generateBodemDeadline }

type BodemPlannedStatus =
  'samenstellen_aanvraag' | 'review' | 'aanvullend_onderzoek_nodig'

/**
 * Enige plek in de app waar de bodem deadlines worden bepaald.
 * Moet gelijk blijven aan `createPlanningBodem` in api/src/subproces/use-cases/subproces-util.js
 *
 * - samenstellen_aanvraag: vanaf vandaag 1 week
 * - review: vanaf vandaag 2 weken
 * - aanvullend_onderzoek_nodig: vanaf de deadline van review 3 weken extra
 */
const BODEM_WEEKS_PER_STATUS = {
  samenstellen_aanvraag: 1,
  review: 2,
  aanvullend_onderzoek_nodig: 3,
} as const satisfies Record<BodemPlannedStatus, number>

const BODEM_STEPS = [
  { status: 'samenstellen_aanvraag' },
  { status: 'review' },
  { status: 'aanvullend_onderzoek_nodig', optional: true },
  { status: 'done' },
] as const satisfies readonly StepConfig[]

const canEditBodem = ({
  user,
  subproces,
}: {
  user: AuthenticatedUser
  subproces: Subproces
}) => canEditSubproces({ user, subproces, mode: 'and' })

const generateBodemStepStatuses = ({ subproces }: { subproces: Subproces }) =>
  generateStepStatuses({ subproces, steps: BODEM_STEPS })

/**
 * @param reviewDueDate deadline van review, alleen gebruikt voor aanvullend_onderzoek_nodig. Valt terug op vandaag.
 */
const generateBodemDeadline = (
  status: BodemPlannedStatus,
  reviewDueDate?: string | Date,
) => {
  const from =
    status === 'aanvullend_onderzoek_nodig' && reviewDueDate
      ? new Date(reviewDueDate)
      : new Date()

  return addWeeks(from, BODEM_WEEKS_PER_STATUS[status])
}
